// docs/116: what the inn's panels share — the side panel (the bar, the bard, the room: by the right
// edge, as the shop's counter is), the close-up scene (the noticeboard, the guestbook, the window: the
// room dims and the thing fills the middle), and the picker for which of my heroes pays or signs.
import { useEffect, type ReactNode } from 'react';
import { Button, Panel, Select } from '../../../../components';
import { playSfx } from '../../../../../core/audio';
import { useInn, type InnPanel } from '../store';
import type { InnData } from '../types';

export type Payer = InnData['payers'][number];

export function closePanel(): void {
  playSfx('ui.close');
  useInn.getState().close();
}

/** A panel by the right edge: a title, a close button and the panel's own rows. */
export function SidePanel({ name, title, children, className = '' }: { name: InnPanel; title: string; children: ReactNode; className?: string }): React.JSX.Element {
  return (
    <Panel variant="solid" className={`fk-innp__side fk-hit ${className}`} label={title} data-testid={`inn-panel-${name}`}>
      <header className="fk-innp__side-head">
        <span>{title}</span>
        <Button small variant="ghost" onClick={closePanel} aria-label={`Close ${title.toLowerCase()}`} data-testid="inn-panel-close">✕</Button>
      </header>
      {children}
    </Panel>
  );
}

/** A close-up: the room dims behind it and a click outside the thing (or Esc) steps back. */
export function Scene({ name, label, children, caption, className = '' }: { name: InnPanel; label: string; children: ReactNode; caption?: ReactNode; className?: string }): React.JSX.Element {
  return (
    <div className={`fk-innp__scene fk-hit ${className}`} role="dialog" aria-label={label} data-testid={`inn-panel-${name}`}
      onClick={(e) => { if (e.target === e.currentTarget) closePanel(); }}>
      <div className="fk-innp__scene-body">
        {children}
        <div className="fk-innp__scene-foot">
          {caption && <p className="fk-innp__caption">{caption}</p>}
          <Button small variant="ghost" onClick={closePanel} data-testid="inn-panel-close">Step back</Button>
        </div>
      </div>
    </div>
  );
}

/** Which of my heroes: a select when there are several, their name when there is one. */
export function HeroPick({ payers, value, onChange, label = 'Who', price, testid }: { payers: readonly Payer[]; value: bigint | undefined; onChange(id: bigint): void; label?: string; price?: number; testid?: string }): React.JSX.Element {
  if (!payers.length) return <span className="fk-innp__faint">None of your heroes is here.</span>;
  if (payers.length === 1) {
    const p = payers[0]!;
    return <span className="fk-innp__pick"><span className="fk-innp__pick-label">{label}</span><b>{p.name}</b>{price !== undefined && <span className="fk-innp__gold">{p.gold}g</span>}</span>;
  }
  return (
    <label className="fk-innp__pick">
      <span className="fk-innp__pick-label">{label}</span>
      <Select value={value?.toString() ?? ''} onChange={(e) => onChange(BigInt(e.target.value))} data-testid={testid}>
        {payers.map((p) => <option key={p.key} value={p.id.toString()} disabled={price !== undefined && p.gold < price}>{p.name} · {p.gold}g</option>)}
      </Select>
    </label>
  );
}

/** The first hero who can pay `price` (or the first at all). */
export const firstPayer = (payers: readonly Payer[], price = 0): Payer | undefined => payers.find((p) => p.gold >= price) ?? payers[0];

/** Play `key` once when the panel opens. */
export function useOpenSound(key: Parameters<typeof playSfx>[0], gain = 0.6): void {
  useEffect(() => { playSfx(key, { gain }); }, [key, gain]);
}
